import { validators } from '@vivaldis/common'
import type { EmployeeInput } from '@vivaldis/graphql'
import type { RcFile } from 'antd/es/upload/interface'
import { format } from 'date-fns'
import { Formik, FormikContextType, useFormikContext } from 'formik'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { fileToBase64 } from '../../../../../utils/fileToBase64'
import { Step, useStep } from '../../../provider/StepProvider'

export interface UploadIdCardFormValues {
  workPermitFrontPictureFile: RcFile | null
  workPermitBackPictureFile: RcFile | null
  workPermitExpiresOn: Date | null
}

export function useForm(): FormikContextType<UploadIdCardFormValues> {
  return useFormikContext()
}

export interface EmployeeFormProviderProps {
  onEmployeeUpdate: (employeeInput: EmployeeInput) => Promise<void>
}

export const FormProvider = ({
  onEmployeeUpdate,
  children
}: PropsWithChildren<EmployeeFormProviderProps>) => {
  const [t] = useTranslation('web_ui')

  const { setStep } = useStep()

  const initialFormValues: UploadIdCardFormValues = useMemo(
    () => ({
      workPermitFrontPictureFile: null,
      workPermitBackPictureFile: null,
      workPermitExpiresOn: null
    }),
    []
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        workPermitFrontPictureFile: Yup.mixed()
          .label(
            t(
              'edit_employee_personal_information_form.steps.add_work_permit.work_permit_front_picture.label'
            )
          )
          .nullable()
          .required(
            t(
              'edit_employee_personal_information_form.steps.add_work_permit.work_permit_front_picture.errors.required'
            )
          ),
        workPermitBackPictureFile: Yup.mixed()
          .label(
            t(
              'edit_employee_personal_information_form.steps.add_work_permit.work_permit_back_picture.label'
            )
          )
          .nullable()
          .required(
            t(
              'edit_employee_personal_information_form.steps.add_work_permit.work_permit_back_picture.errors.required'
            )
          ),
        workPermitExpiresOn: Yup.date()
          .label(
            t(
              'edit_employee_personal_information_form.steps.add_work_permit.work_permit_expires_on.label'
            )
          )
          .required()
          .test(
            'isWorkPermitNotExpired',
            t(
              'edit_employee_personal_information_form.steps.add_work_permit.work_permit_expires_on.errors.expired'
            ),
            (workPermitExpiresOn: Date | null) => {
              if (!workPermitExpiresOn) {
                return false
              }
              return validators.isDateInTheFuture(workPermitExpiresOn)
            }
          )
      }),
    [t]
  )

  const handleSubmit = useCallback(
    async (values: UploadIdCardFormValues) => {
      await onEmployeeUpdate({
        workProfile: {
          workPermitBackPictureAttachment: values.workPermitBackPictureFile
            ? {
                fileName: values.workPermitBackPictureFile.name,
                base64: await fileToBase64(values.workPermitBackPictureFile)
              }
            : null,
          workPermitFrontPictureAttachment: values.workPermitFrontPictureFile
            ? {
                fileName: values.workPermitFrontPictureFile.name,
                base64: await fileToBase64(values.workPermitFrontPictureFile)
              }
            : null,
          workPermitExpiresOn: values.workPermitExpiresOn
            ? format(values.workPermitExpiresOn, 'yyyy-MM-dd')
            : null
        }
      })
      setStep(Step.OcrSucceededStep)
    },
    [onEmployeeUpdate, setStep]
  )

  return (
    <Formik<UploadIdCardFormValues>
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
